import {collection, onSnapshot, type Unsubscribe} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import {env} from '../../constants';
import {firebaseAuth, firebaseFirestore} from '../../setup/firebase';
import type {GenerationJobProject} from './schema';

export default function useListGenerationJobProjects() {
  const [uid, setUid] = useState<string | null>(null);
  const [projects, setProjects] = useState<GenerationJobProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const unsubAuth = firebaseAuth.onAuthStateChanged((user) => {
      setUid(user?.uid ?? null);
    });
    return () => {
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    if (!uid) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const collRef = collection(firebaseFirestore, 'user', uid, env.collection.generationJobProject);
    const unsub: Unsubscribe = onSnapshot(
      collRef,
      (snap) => {
        const list: GenerationJobProject[] = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          list.push({
            id: d.id,
            name: typeof data.name === 'string' ? data.name : undefined,
            gdriveTokenId: typeof data.gdriveTokenId === 'string' ? data.gdriveTokenId : '',
            sourceImageFileId: typeof data.sourceImageFileId === 'string' ? data.sourceImageFileId : undefined,
            sourceImageUrl: typeof data.sourceImageUrl === 'string' ? data.sourceImageUrl : undefined,
            sheetId: typeof data.sheetId === 'string' ? data.sheetId : '',
            outputFolderId: typeof data.outputFolderId === 'string' ? data.outputFolderId : '',
            creationTimestamp: typeof data.creationTimestamp === 'number' ? data.creationTimestamp : 0,
            systemPrompt: typeof data.systemPrompt === 'string' ? data.systemPrompt : undefined,
          });
        });
        setProjects(list);
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    );
    return () => unsub();
  }, [uid]);

  const refetch = () => {
    // onSnapshot keeps data in sync; no explicit refetch needed
  };

  return {
    projects,
    loading,
    error,
    refetch,
  };
}
